import { useMutation } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { Check, Pencil, X } from 'lucide-react'
import { ROOM_NAME_MAX_LENGTH } from '@repo/sdk'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

export function RoomNameEditor({
  roomId,
  name,
  canEdit,
  onSaved,
}: {
  roomId: string | null
  name: string | null
  canEdit: boolean
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const { t } = useI18n()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name ?? '')

  useEffect(() => {
    if (!editing)
      setDraft(name ?? '')
  }, [name, editing])

  const { mutateAsync, isPending } = useMutation(
    trpc.roomRouter.rename.mutationOptions(),
  )

  if (!canEdit) {
    if (!name)
      return null
    return (
      <p className="text-sm font-medium" data-testid="room-name">
        {name}
      </p>
    )
  }

  const save = async () => {
    if (!roomId || isPending)
      return
    const normalizedName = draft.trim()
    if (!normalizedName) {
      toast.error(t('game.error.room_name_required'))
      return
    }
    try {
      await mutateAsync({ roomId, name: normalizedName })
      setEditing(false)
      onSaved()
    }
    catch (error) {
      const key = error instanceof TRPCClientError ? error.message : 'room_rename_failed'
      const translated = t(`game.error.${key}`)
      toast.error(translated === `game.error.${key}` ? t('game.error.room_rename_failed') : translated)
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <p
          className="min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground"
          data-testid="room-name"
        >
          {name || t('game.room.namePlaceholder')}
        </p>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid="room-name-edit"
          onClick={() => setEditing(true)}
        >
          <Pencil className="size-4" aria-hidden="true" />
          <span className="sr-only">{t('game.room.nameEdit')}</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        autoFocus
        value={draft}
        required
        maxLength={ROOM_NAME_MAX_LENGTH}
        placeholder={t('game.room.namePlaceholder')}
        aria-label={t('game.room.nameLabel')}
        data-testid="room-name-field"
        onChange={event => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing)
            return
          if (event.key === 'Enter') {
            event.preventDefault()
            void save()
          }
          if (event.key === 'Escape') {
            setDraft(name ?? '')
            setEditing(false)
          }
        }}
      />
      <Button
        type="button"
        size="sm"
        disabled={isPending || !draft.trim()}
        data-testid="room-name-save"
        onClick={() => { void save() }}
      >
        <Check className="size-4" aria-hidden="true" />
        <span className="sr-only">{t('common.save')}</span>
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={isPending}
        onClick={() => {
          setDraft(name ?? '')
          setEditing(false)
        }}
      >
        <X className="size-4" aria-hidden="true" />
        <span className="sr-only">{t('common.cancel')}</span>
      </Button>
    </div>
  )
}
